import {
  ChangeDetectionStrategy, Component, EventEmitter, Output, signal, inject, OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminService, AdminForbiddenError } from '../../services/admin.service.js';
import type { DashboardKPI } from '../../models/admin.types.js';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6 space-y-6">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-bold text-gray-200 uppercase tracking-wider">Обзор</h3>
        <button (click)="reload()" [disabled]="loading()" class="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 text-xs font-semibold rounded border border-gray-700 transition-colors">
          {{ loading() ? 'Загружаю…' : 'Обновить' }}
        </button>
      </div>

      @if (error()) {
        <div class="p-3 bg-red-950/40 border border-red-900/60 rounded text-sm text-red-300">{{ error() }}</div>
      }

      @if (data(); as d) {
        <!-- KPI grid -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Всего пользователей</p>
            <p class="text-2xl font-bold text-gray-100 mt-1">{{ d.users.total }}</p>
            <p class="text-xs text-gray-400 mt-0.5">удалено: {{ d.users.deleted }}</p>
          </div>
          <div class="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Pro</p>
            <p class="text-2xl font-bold text-purple-300 mt-1">{{ d.users.pro }}</p>
            <p class="text-xs text-gray-400 mt-0.5">{{ d.users.proPercent }}% конверсия</p>
          </div>
          <div class="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Активных сессий</p>
            <p class="text-2xl font-bold text-emerald-300 mt-1">{{ d.sessions.active }}</p>
          </div>
          <div class="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">152-ФЗ согласие</p>
            <p class="text-2xl font-bold text-emerald-300 mt-1">{{ d.consent.okPercent }}%</p>
            <p class="text-xs text-gray-400 mt-0.5">{{ d.consent.ok }} / {{ d.consent.total }}</p>
          </div>

          <div class="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Активны 24ч</p>
            <p class="text-2xl font-bold text-gray-100 mt-1">{{ d.active.last24h }}</p>
          </div>
          <div class="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Активны 7д</p>
            <p class="text-2xl font-bold text-gray-100 mt-1">{{ d.active.last7d }}</p>
          </div>
          <div class="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Активны 30д</p>
            <p class="text-2xl font-bold text-gray-100 mt-1">{{ d.active.last30d }}</p>
          </div>
          <div class="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Регистраций</p>
            <p class="text-2xl font-bold text-gray-100 mt-1">{{ d.signups.today }}</p>
            <p class="text-xs text-gray-400 mt-0.5">сегодня · {{ d.signups.last7d }} за 7д</p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div class="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <p class="text-xs text-gray-300 uppercase tracking-wider font-semibold">Доход Boosty (30д)</p>
            <p class="text-xl font-bold text-amber-300 mt-1">{{ d.revenue.boostyLast30dRub }} ₽</p>
            <p class="text-xs text-gray-500 mt-1">плейсхолдер пока Boosty не подключен</p>
          </div>
        </div>

        <!-- Recent admin actions -->
        <div>
          <h3 class="text-sm font-bold text-gray-200 uppercase tracking-wider mb-3">Последние действия</h3>
          @if (d.recentAdminActions.length === 0) {
            <p class="text-sm text-gray-500 italic">Журнал пуст.</p>
          } @else {
            <div class="bg-gray-950 border border-gray-800 rounded-lg divide-y divide-gray-800">
              @for (e of d.recentAdminActions; track e.id) {
                <div class="p-3 text-xs flex items-center gap-3">
                  <span class="text-gray-500 font-mono w-40 shrink-0">{{ formatDate(e.created_at) }}</span>
                  <span class="px-2 py-0.5 rounded bg-gray-900 border border-gray-800 text-gray-300 font-mono shrink-0">{{ e.action }}</span>
                  <span class="text-gray-400 truncate">{{ e.actor_email || e.actor_uid || '—' }}</span>
                  @if (e.target_uid) {
                    <span class="text-gray-600 shrink-0">→</span>
                    <span class="text-gray-400 truncate font-mono">{{ e.target_uid }}</span>
                  }
                </div>
              }
            </div>
          }
        </div>
      } @else if (loading()) {
        <p class="text-sm text-gray-500 italic">Загрузка…</p>
      }
    </div>
  `,
})
export class AdminDashboardComponent implements OnInit {
  @Output() forbidden = new EventEmitter<void>();
  private adminService = inject(AdminService);

  data = signal<DashboardKPI | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);

  ngOnInit() {
    this.reload();
  }

  async reload() {
    this.loading.set(true);
    this.error.set(null);
    try {
      const d = await this.adminService.getDashboard();
      this.data.set(d);
    } catch (err: any) {
      if (err instanceof AdminForbiddenError) { this.forbidden.emit(); return; }
      this.error.set(err?.message || 'Ошибка загрузки');
    } finally {
      this.loading.set(false);
    }
  }

  formatDate(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'medium' });
    } catch { return iso; }
  }
}
